import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Clock, ShieldCheck, Star, Truck } from "lucide-react";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { LANDING_PAGES } from "../data/landingPages";

const TRUST = [
  { icon: Truck, label: "30–90 min delivery" },
  { icon: ShieldCheck, label: "Discreet & secure" },
  { icon: BadgeCheck, label: "21+ verified" },
  { icon: Star, label: "4.9 · 1,251 Google reviews" },
];

export default function Landing({ data }) {
  const [products, setProducts] = useState([]);
  const [quickView, setQuickView] = useState(null);

  useEffect(() => {
    setProducts([]);
    fetchProducts({ category: data.category || undefined, in_stock: true, limit: 8 })
      .then((d) => setProducts(d.items))
      .catch(() => {});
  }, [data]);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [data]);

  const others = LANDING_PAGES.filter((p) => p.slug !== data.slug).slice(0, 6);

  return (
    <main data-testid={`landing-${data.slug}`}>
      <Seo title={data.title} description={data.description} />

      <section className="relative bg-brand-ink py-20 text-white grain sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">Farmacy District DC</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
              {data.h1}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">{data.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={data.category ? `/category/${data.category}` : "/shop"}
                data-testid="landing-shop-cta"
                className="rounded-full bg-brand-lime px-9 py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
              >
                SHOP NOW
              </Link>
              <Link
                to="/category/special-deals"
                className="rounded-full border border-white/40 px-9 py-4 text-sm font-bold tracking-wide text-white transition-colors hover:border-white hover:bg-white/10"
                data-testid="landing-deals-cta"
              >
                VIEW DEALS
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {TRUST.map((t) => (
                <span key={t.label} className="flex items-center gap-2 text-xs font-semibold text-neutral-300">
                  <t.icon className="h-4 w-4 text-brand-lime" /> {t.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {data.bullets.map((b, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex h-full items-start gap-3 border border-neutral-200 bg-white p-6">
                <Clock className="h-5 w-5 flex-shrink-0 text-brand-red" />
                <p className="text-sm font-semibold leading-relaxed text-neutral-700">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {products.length > 0 && (
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <Reveal>
              <div className="mb-10 flex items-end justify-between">
                <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  {data.category ? "Shop This Category" : "Popular Right Now"}
                </h2>
                <Link
                  to={data.category ? `/category/${data.category}` : "/shop"}
                  className="hidden items-center gap-1.5 text-sm font-bold text-brand-red sm:flex"
                  data-testid="landing-view-all"
                >
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 gap-4 gap-y-10 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.product_id} product={p} onQuickView={setQuickView} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <h2 className="mb-8 font-display text-3xl font-bold tracking-tight sm:text-4xl">FAQs</h2>
          <Accordion type="single" collapsible>
            {data.faqs.map((f, i) => (
              <AccordionItem key={i} value={`lf-${i}`} data-testid={`landing-faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-base font-bold tracking-tight sm:text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-neutral-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section className="bg-brand-sand py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Cannabis Delivery Across DC</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  data-testid={`landing-link-${p.slug}`}
                  className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-semibold transition-colors hover:border-brand-ink"
                >
                  {p.h1}
                </Link>
              ))}
            </div>
            <div className="mt-10 bg-brand-ink p-10 text-center text-white grain">
              <h3 className="font-display text-2xl font-black tracking-tight sm:text-3xl">Ready to Order?</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">
                Premium cannabis, delivered fast and discreet across Washington, DC. Adults 21+.
              </p>
              <Link
                to="/shop"
                data-testid="landing-bottom-cta"
                className="mt-6 inline-block rounded-full bg-brand-lime px-9 py-4 text-sm font-bold text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
              >
                ORDER NOW
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <QuickView slug={quickView} onClose={() => setQuickView(null)} />
    </main>
  );
}
